import React from 'react';
import { Box, Card, Skeleton, Stack } from '@mui/material';
import { tokens } from '../../theme/tokens';

/** Placeholder rows for a table that has not loaded. */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number; rowHeight?: number }> = ({
  rows = 6,
  columns = 4,
  rowHeight = 20,
}) => (
  <Box role="status" aria-label="Loading">
    {Array.from({ length: rows }).map((_, row) => (
      <Box
        key={row}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${tokens.line.hairline}`,
        }}
      >
        {Array.from({ length: columns }).map((__, col) => (
          <Skeleton key={col} variant="text" height={rowHeight} width={col === 0 ? '70%' : '50%'} />
        ))}
      </Box>
    ))}
  </Box>
);

/** Placeholder for a list of single-line rows. */
export const ListSkeleton: React.FC<{ rows?: number; rowHeight?: number }> = ({ rows = 5, rowHeight = 44 }) => (
  <Stack role="status" aria-label="Loading">
    {Array.from({ length: rows }).map((_, row) => (
      <Box key={row} sx={{ px: 2, py: 1, borderBottom: `1px solid ${tokens.line.hairline}` }}>
        <Skeleton variant="rounded" height={rowHeight - 16} />
      </Box>
    ))}
  </Stack>
);

/** Placeholder card with a title line and a few body lines. */
export const CardSkeleton: React.FC<{ lines?: number; height?: number }> = ({ lines = 3, height }) => (
  <Card sx={{ p: 2.5, minHeight: height }} role="status" aria-label="Loading">
    <Skeleton variant="text" width="40%" height={24} />
    {Array.from({ length: lines }).map((_, line) => (
      <Skeleton key={line} variant="text" width={line === lines - 1 ? '55%' : '90%'} />
    ))}
  </Card>
);

/** A grid of `CardSkeleton`s. */
export const CardGridSkeleton: React.FC<{ count?: number; minWidth?: number; lines?: number }> = ({
  count = 6,
  minWidth = 240,
  lines = 3,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: `repeat(auto-fill, minmax(${minWidth}px, 1fr))` },
      gap: 2,
    }}
  >
    {Array.from({ length: count }).map((_, i) => (
      <CardSkeleton key={i} lines={lines} />
    ))}
  </Box>
);
